import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../api/client';
import { User, Mail, Phone, Bell, Shield, LogOut, CreditCard, Zap, ArrowUpRight, TrendingUp, Car as CarIcon, Gift, Copy, Users as UsersIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import ReferralMilestoneBadges from '../components/ReferralMilestoneBadges';

const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [referralLoading, setReferralLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    notification_preferences: user?.notification_preferences || {
      email: true,
      sms: false,
      push: false
    }
  });

  const subscription = user?.subscription || {};
  const tier = subscription.tier || 'free';
  const status = subscription.status || 'inactive';
  const isActive = status === 'active';

  const tierLabels = {
    free: 'Free',
    premium: 'Premium',
    family: 'Family',
    fleet: 'Fleet'
  };

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/referrals/my-code`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setReferralData(data);
      }
    } catch (error) {
      console.error('Referral fetch error:', error);
    } finally {
      setReferralLoading(false);
    }
  };

  const copyReferralLink = () => {
    if (referralData?.share_url) {
      navigator.clipboard.writeText(referralData.share_url);
      toast.success('Referral link copied to clipboard!');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await userAPI.updateProfile(formData);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/billing/create-portal-session`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to create portal session');
      }

      const data = await response.json();
      window.location.href = data.portal_url;
    } catch (error) {
      console.error('Portal error:', error);
      toast.error('Failed to open billing portal. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6 pb-6 max-w-4xl"
    >
      {/* Header */}
      <div>
        <h1 className="text-4xl font-semibold tracking-tight">Profile</h1>
        <p className="text-muted-foreground mt-1">Manage your account settings and preferences</p>
      </div>

      {/* Profile Info Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="card-elevated">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-8 w-8 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl">{user?.name}</CardTitle>
                <CardDescription className="text-base">{user?.email}</CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      </motion.div>


      {/* Subscription Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        <Card className="card-elevated">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Subscription
                </CardTitle>
                <CardDescription>Manage your subscription and billing</CardDescription>
              </div>
              <Badge variant={isActive ? 'default' : 'secondary'} className="capitalize">
                {tierLabels[tier] || 'Free'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Current Plan</p>
                <p className="text-lg font-medium capitalize">{tierLabels[tier]} Plan</p>
              </div>

              {tier !== 'free' && isActive && (
                <>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Status</p>
                    <Badge variant="default" className="bg-green-500">Active</Badge>
                  </div>

                  {subscription.current_period_end && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Renewal Date</p>
                      <p className="text-sm">
                        {new Date(subscription.current_period_end).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  )}
                </>
              )}

              <div className="flex gap-3 pt-2">
                {tier === 'free' ? (
                  <Button
                    onClick={() => navigate('/pricing')}
                    className="flex items-center gap-2"
                    data-testid="profile-upgrade-button"
                  >
                    <Zap className="h-4 w-4" />
                    Upgrade Plan
                  </Button>
                ) : (
                  <Button
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                    variant="outline"
                    className="flex items-center gap-2"
                    data-testid="profile-manage-subscription-button"
                  >
                    {portalLoading ? (
                      'Loading...'
                    ) : (
                      <>
                        Manage Subscription
                        <ArrowUpRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                )}
                <Button
                  onClick={() => navigate('/pricing')}
                  variant="ghost"
                  className="text-primary"
                  data-testid="profile-view-all-plans-button"
                >
                  View All Plans
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>


      {/* Usage Stats Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Usage & Limits
            </CardTitle>
            <CardDescription>Track your usage across features</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* AI Scans Usage */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">AI Document Scans</p>
                <p className="text-sm text-muted-foreground">
                  {subscription.max_ai_scans_per_month === -1 
                    ? 'Unlimited' 
                    : `${subscription.ai_scans_used_month || 0}/${subscription.max_ai_scans_per_month || 3}`
                  }
                </p>
              </div>
              {subscription.max_ai_scans_per_month !== -1 && (
                <>
                  <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all duration-300"
                      style={{ 
                        width: `${Math.min(((subscription.ai_scans_used_month || 0) / (subscription.max_ai_scans_per_month || 3)) * 100, 100)}%` 
                      }}
                    />
                  </div>
                  {subscription.ai_scans_reset_at && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Resets on {new Date(subscription.ai_scans_reset_at).toLocaleDateString('en-GB')}
                    </p>
                  )}
                </>
              )}
              {subscription.max_ai_scans_per_month === -1 && (
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="secondary" className="bg-green-500/10 text-green-600 border-green-500/20">
                    Unlimited scans
                  </Badge>
                </div>
              )}
            </div>

            {/* Vehicles Usage */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">Vehicles</p>
                <p className="text-sm text-muted-foreground">
                  0/{subscription.max_vehicles || 1}
                </p>
              </div>
              <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${(0 / (subscription.max_vehicles || 1)) * 100}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {tier === 'free' 
                  ? 'Upgrade to add more vehicles' 
                  : `${subscription.max_vehicles - 0} slots available`
                }
              </p>
            </div>

            {/* Show user limit for Family/Fleet tiers */}
            {(tier === 'family' || tier === 'fleet') && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">User Accounts</p>
                  <p className="text-sm text-muted-foreground">
                    1/{subscription.max_users || 1}


      {/* Referral Program Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
      >
        <Card className="card-elevated">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Gift className="h-5 w-5" />
                  Referral Program
                </CardTitle>
                <CardDescription>Invite friends and earn rewards</CardDescription>
              </div>
              <Badge variant="secondary" className="bg-green-500/10 text-green-600 border-green-500/20">
                Free Month Every 3 Referrals
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {referralLoading ? (
              <Skeleton className="h-20" />
            ) : (
              <>
                {/* Referral Link */}
                <div>
                  <Label htmlFor="referral-link" className="text-sm font-medium mb-2 block">
                    Your Referral Link
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="referral-link"
                      value={referralData?.share_url || ''}
                      readOnly
                      className="font-mono text-sm"
                      data-testid="referral-link-input"
                    />
                    <Button
                      onClick={copyReferralLink}
                      variant="outline"
                      size="icon"
                      data-testid="copy-referral-link"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Share this link with friends to earn rewards when they upgrade to a paid plan
                  </p>
                </div>

                {/* Referral Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 rounded-lg bg-muted/30">
                    <UsersIcon className="h-6 w-6 mx-auto mb-1 text-primary" />
                    <p className="text-2xl font-bold">{referralData?.referrals_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Referrals</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/30">
                    <Zap className="h-6 w-6 mx-auto mb-1 text-primary" />
                    <p className="text-2xl font-bold">{referralData?.referrals_converted || 0}</p>
                    <p className="text-xs text-muted-foreground">Conversions</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/30">
                    <Gift className="h-6 w-6 mx-auto mb-1 text-primary" />
                    <p className="text-2xl font-bold">{referralData?.reward_credits || 0}</p>
                    <p className="text-xs text-muted-foreground">Rewards Earned</p>
                  </div>
                </div>

                {/* Milestone Badges */}
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium mb-3">Achievements:</p>
                  <ReferralMilestoneBadges referralCount={referralData?.referrals_count || 0} />
                </div>

                {/* How It Works */}
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium mb-3">How It Works:</p>
                  <ol className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">1</span>
                      <span>Share your unique referral link with friends</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">2</span>
                      <span>They sign up and upgrade to any paid plan</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">3</span>
                      <span>You earn 1 free month for every 3 conversions</span>
                    </li>
                  </ol>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>

                  </p>
                </div>
                <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${(1 / (subscription.max_users || 1)) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {subscription.max_users - 1} user slots available
                </p>
              </div>
            )}

            {/* Upgrade CTA for free tier */}
            {tier === 'free' && (
              <div className="pt-2 border-t">
                <p className="text-sm text-muted-foreground mb-3">
                  Unlock unlimited features and grow your vehicle fleet
                </p>
                <Button 
                  onClick={() => navigate('/pricing')}
                  variant="outline" 
                  className="w-full"
                  size="sm"
                >
                  View Premium Plans
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>


      {/* Personal Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
            <CardDescription>Update your personal details</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10"
                    data-testid="profile-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="email"
                    value={user?.email || ''}
                    disabled
                    className="pl-10 bg-muted"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Email cannot be changed (used for account login)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number (Optional)</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="pl-10"
                    placeholder="+44 123 456 7890"
                    data-testid="profile-phone-input"
                  />
                </div>
              </div>

              <Button type="submit" disabled={loading} className="rounded-xl" data-testid="update-profile-button">
                {loading ? 'Updating...' : 'Update Profile'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>

      {/* Notification Preferences */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card className="card-elevated">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" />
              <CardTitle>Notification Preferences</CardTitle>
            </div>
            <CardDescription>Choose how you want to receive reminders</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b">
              <div className="space-y-0.5">
                <Label htmlFor="email-notif">Email Notifications</Label>
                <p className="text-sm text-muted-foreground">Receive reminders via email</p>
              </div>
              <Switch
                id="email-notif"
                checked={formData.notification_preferences.email}
                onCheckedChange={(checked) =>
                  setFormData({
                    ...formData,
                    notification_preferences: { ...formData.notification_preferences, email: checked }
                  })
                }
                data-testid="email-notifications-toggle"
              />
            </div>

            <div className="flex items-center justify-between py-3 border-b">
              <div className="space-y-0.5">
                <Label htmlFor="sms-notif">SMS Notifications</Label>
                <p className="text-sm text-muted-foreground">Receive text message reminders</p>
              </div>
              <Switch
                id="sms-notif"
                checked={formData.notification_preferences.sms}
                onCheckedChange={(checked) =>
                  setFormData({
                    ...formData,
                    notification_preferences: { ...formData.notification_preferences, sms: checked }
                  })
                }
                data-testid="sms-notifications-toggle"
              />
            </div>

            <div className="flex items-center justify-between py-3">
              <div className="space-y-0.5">
                <Label htmlFor="push-notif">Push Notifications</Label>
                <p className="text-sm text-muted-foreground">Browser notifications for urgent reminders</p>
              </div>
              <Switch
                id="push-notif"
                checked={formData.notification_preferences.push}
                onCheckedChange={(checked) =>
                  setFormData({
                    ...formData,
                    notification_preferences: { ...formData.notification_preferences, push: checked }
                  })
                }
                data-testid="push-notifications-toggle"
              />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Account Details */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="card-elevated">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <CardTitle>Account Details</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Account ID</span>
              <span className="font-mono text-sm">{user?.user_id}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Authentication</span>
              <span className="font-medium">Email & Password</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Member Since</span>
              <span className="font-medium">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Logout */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <Card className="border-destructive/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold mb-1">Sign Out</h3>
                <p className="text-sm text-muted-foreground">Sign out of your VehicleBuddy account</p>
              </div>
              <Button variant="destructive" onClick={handleLogout} className="rounded-xl" data-testid="logout-button">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
};

export default Profile;
